import { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Typography } from 'antd';
import InputMask from 'react-input-mask';
import OTPInput from 'react-otp-input';
import { useTranslation } from 'react-i18next';

import {
	postAuthCodeSend,
	postAuthCodeValidate,
	postEmailCodeSend,
	postEmailCodeValidate,
} from 'store/slices/customerSlice';
import { batchAddToCart } from 'store/slices/cartSlice';
import {
	getGuestCartFromStorage,
	clearGuestCartStorage,
} from 'helpers/guestCart';
import useCustomer from 'hooks/useCustomer';

/** TODO: false когда SMS снова включим */
const TEMP_SKIP_SMS_AUTH = true;
/** TODO: false когда email-коды снова включим */
const TEMP_SKIP_EMAIL_AUTH = true;

const isValidEmail = value => /\S+@\S+\.\S+/.test(value);

const SignIn = () => {
	const { t } = useTranslation();
	const [loginTab, setLoginTab] = useState('phone'); // 'phone' | 'email'

	const [status, setStatus] = useState({ type: '', text: '' });
	const [telephone, setTelephone] = useState('');
	const [code, setCode] = useState();
	const [method, setMethod] = useState('sms');

	const [email, setEmail] = useState('');
	const [emailCodeSent, setEmailCodeSent] = useState(false);

	const { setCustomer, customer } = useCustomer();

	const handleTelephone = e => {
		const clean = e.target.value.replace(/\(|\)|-| /g, () => '');
		setTelephone(clean.split('_')[0]);
	};

	const completeLogin = async res => {
		setCustomer({
			...res.customer_info,
			store_id: customer.store_id || 0,
		});
		const guestItems = getGuestCartFromStorage();
		if (guestItems.length > 0) {
			try {
				await batchAddToCart(
					guestItems.map(i => ({
						product_id: i.product_id,
						quantity: i.quantity,
					})),
				);
				clearGuestCartStorage();
			} catch (e) {
				console.error('Merge guest cart failed', e);
			}
		}
		window.location.reload();
	};

	const handleSubmitTelephone = async () => {
		setStatus({
			type: 'info',
			text: TEMP_SKIP_SMS_AUTH ? t('auth.checkingCode') : (
				method === 'whatsapp'
					? t('auth.codeSentWhatsapp')
					: t('auth.codeSentSms')
			),
		});
		const res = await postAuthCodeSend(telephone, method);
		if (res.status === 'error') {
			setStatus({ type: 'error', text: res.text });
			return;
		}
		if (TEMP_SKIP_SMS_AUTH && res.status === 'success' && res.customer_info) {
			await completeLogin(res);
			return;
		}
		setCode(res);
		setStatus();
	};

	const handleCode = async value => {
		if (value.length === 4) {
			setStatus({ type: 'info', text: t('auth.checkingCode') });
			const res = await postAuthCodeValidate(code?.telephone || telephone, value);
			if (res.status === 'success') {
				setStatus({ type: 'info', text: res.text });
				await completeLogin(res);
			} else {
				setStatus({ type: 'error', text: res.text });
			}
		}
	};

	const handleClearCode = () => {
		setCode();
	};

	const handleEmailChange = e => {
		setEmail(e.target.value.trim());
	};

	const handleSubmitEmail = async () => {
		setStatus({
			type: 'info',
			text: TEMP_SKIP_EMAIL_AUTH
				? t('auth.checkingCode')
				: t('auth.sendingCodeEmail'),
		});
		const res = await postEmailCodeSend(email);
		if (res.status === 'error') {
			setStatus({ type: 'error', text: res.text });
			return res;
		}
		if (TEMP_SKIP_EMAIL_AUTH && res.status === 'success' && res.customer_info) {
			await completeLogin(res);
			return res;
		}
		setEmailCodeSent(true);
		setStatus();
		return res;
	};

	const handleEmailCode = async value => {
		if (value.length === 4) {
			setStatus({ type: 'info', text: t('auth.checkingCode') });
			const res = await postEmailCodeValidate(email, value);
			if (res.status === 'success') {
				setStatus({ type: 'info', text: res.text });
				await completeLogin(res);
			} else {
				setStatus({ type: 'error', text: res.text });
			}
		}
	};

	const handleClearEmailCode = () => {
		setEmailCodeSent(false);
	};

	const switchTab = tab => {
		setLoginTab(tab);
		setStatus();
		setCode();
		setEmailCodeSent(false);
	};

	return (
		<div style={{ height: 'auto' }}>
			<LoginTabs loginTab={loginTab} switchTab={switchTab} />
			{loginTab === 'email' ? (
				emailCodeSent && !TEMP_SKIP_EMAIL_AUTH ? (
					<EmailCodeForm
						handleCode={handleEmailCode}
						handleClearCode={handleClearEmailCode}
						email={email}
						status={status}
						setStatus={setStatus}
						resend={handleSubmitEmail}
					/>
				) : (
					<EmailForm
						handleSubmitEmail={handleSubmitEmail}
						handleEmailChange={handleEmailChange}
						email={email}
						status={status}
						tempSkipEmail={TEMP_SKIP_EMAIL_AUTH}
					/>
				)
			) : code && !TEMP_SKIP_SMS_AUTH ? (
				<CodeForm
					handleCode={handleCode}
					handleClearCode={handleClearCode}
					code={code}
					status={status}
					method={method}
					setStatus={setStatus}
				/>
			) : (
				<TelephoneForm
					handleSubmitTelephone={handleSubmitTelephone}
					handleTelephone={handleTelephone}
					telephone={telephone}
					status={status}
					method={method}
					setMethod={setMethod}
					tempSkipSms={TEMP_SKIP_SMS_AUTH}
				/>
			)}
		</div>
	);
};

const LoginTabs = ({ loginTab, switchTab }) => {
	const { t } = useTranslation();

	return (
		<Button.Group style={{ width: '100%', marginTop: 24, marginBottom: 16 }}>
			<Button
				type={loginTab === 'phone' ? 'primary' : 'default'}
				onClick={() => switchTab('phone')}
				style={{ width: '50%' }}
			>
				{t('auth.tabPhone')}
			</Button>
			<Button
				type={loginTab === 'email' ? 'primary' : 'default'}
				onClick={() => switchTab('email')}
				style={{ width: '50%' }}
			>
				{t('auth.tabEmail')}
			</Button>
		</Button.Group>
	);
};

const CodeForm = ({ handleCode, handleClearCode, code, status, method, setStatus }) => {
	const { t } = useTranslation();

	function fmtMSS(s) {
		return (s - (s %= 60)) / 60 + (9 < s ? ':' : ':0') + s;
	}

	const AgainCode = () => {
		const [timer, setTimer] = useState(45);
		const handleSubmitTelephone = async () => {
			setTimer(45);
			const res = await postAuthCodeSend(code.telephone, method);
			if (res.status === 'error') {
				setStatus({ type: 'error', text: res.text });
			}
		};
		useEffect(() => {
			const interval = setInterval(() => {
				if (timer > 0) setTimer(timer - 1);
			}, 1000);
			return () => clearInterval(interval);
		}, [timer]);

		return (
			<p>
				{timer === 0 ? (
					<Button type='primary' size='large' onClick={handleSubmitTelephone}>
						{t('auth.resendCode')}
					</Button>
				) : (
					<a href='#' onClick={e => e.preventDefault()}>
						{t('auth.resendCodeTimer', { time: fmtMSS(timer) })}
					</a>
				)}
			</p>
		);
	};

	return (
		<Form name='signin-code' layout='vertical'>
			<Typography.Title
				level={5}
				className='text-gray'
				style={{ marginTop: 0, marginBottom: 12 }}
			>
				{t('auth.codeSentTo')}
				{method === 'whatsapp'
					? ` ${t('auth.viaWhatsapp')}`
					: ` ${t('auth.viaSms')}`}{' '}
				{t('auth.toNumber')}
				<br />
				{code.telephone}
			</Typography.Title>
			<Form.Item
				name='code'
				label={
					method === 'whatsapp'
						? t('auth.codeFromWhatsapp')
						: t('auth.codeFromSms')
				}
				rules={[{ required: true, message: t('auth.enterCode') }]}
				style={{ marginBottom: 16 }}
			>
				<OTPInput
					onChange={handleCode}
					numInputs={4}
					inputType='number'
					shouldAutoFocus
					renderSeparator={<span className='ml-10 mr-10'>-</span>}
					renderInput={props => (
						<Input
							{...props}
							className='rn-otp-input'
							disabled={status && status.type === 'info'}
						/>
					)}
				/>
			</Form.Item>
			<AgainCode />
			{status?.type && (
				<Alert message={status.text} type={status.type} banner />
			)}
		</Form>
	);
};

const TelephoneForm = ({
	handleSubmitTelephone,
	handleTelephone,
	telephone,
	status,
	method,
	setMethod,
	tempSkipSms,
}) => {
	const { t } = useTranslation();

	return (
		<Form
			name='signin-telephone'
			layout='vertical'
			onFinish={handleSubmitTelephone}
		>
			<Typography.Title level={2} style={{ marginBottom: 8 }}>
				{t('auth.loginOrRegister')}
				<Typography.Title
					level={5}
					className='text-gray'
					style={{ marginTop: 8, marginBottom: 0 }}
				>
					{t('auth.loginSubtitle')}
				</Typography.Title>
			</Typography.Title>
			<Form.Item
				name='telephone'
				label={t('auth.yourPhone')}
				style={{ marginBottom: 16 }}
			>
				<InputMask
					mask='+7 (999) 999-99-99'
					onChange={handleTelephone}
					disabled={status?.type === 'info'}
				>
					<Input />
				</InputMask>
			</Form.Item>

			<Form.Item label={t('auth.howToSendCode')} style={{ marginBottom: 16, display: tempSkipSms ? 'none' : undefined }}>
				<Button.Group style={{ width: '100%' }}>
					<Button
						type={method === 'sms' ? 'primary' : 'default'}
						onClick={() => setMethod('sms')}
						disabled={status?.type === 'info'}
						style={{ width: '50%' }}
					>
						📱 SMS
					</Button>
					<Button
						type={method === 'whatsapp' ? 'primary' : 'default'}
						onClick={() => setMethod('whatsapp')}
						disabled={status?.type === 'info'}
						style={{ width: '50%' }}
					>
						💬 МАХ
					</Button>
				</Button.Group>
			</Form.Item>

			<Form.Item style={{ marginBottom: 12 }}>
				<Button
					type='primary'
					htmlType='submit'
					block
					size='large'
					disabled={telephone.length < 12}
					loading={status?.type === 'info'}
				>
					{tempSkipSms ? 'Войти' : t('auth.getCode')}
				</Button>
			</Form.Item>
			<p style={{ marginBottom: 8, marginTop: 0 }}>
				{t('auth.privacyAgreement')}
			</p>
			{status?.type && (
				<Alert message={status.text} type={status.type} banner />
			)}
		</Form>
	);
};

const EmailForm = ({
	handleSubmitEmail,
	handleEmailChange,
	email,
	status,
	tempSkipEmail,
}) => {
	const { t } = useTranslation();

	return (
		<Form name='signin-email' layout='vertical' onFinish={handleSubmitEmail}>
			<Typography.Title level={2} style={{ marginBottom: 8 }}>
				{t('auth.loginOrRegister')}
				<Typography.Title
					level={5}
					className='text-gray'
					style={{ marginTop: 8, marginBottom: 0 }}
				>
					{t('auth.loginSubtitle')}
				</Typography.Title>
			</Typography.Title>
			<Form.Item
				name='email'
				label={t('auth.yourEmail')}
				style={{ marginBottom: 16 }}
			>
				<Input
					type='email'
					placeholder='example@mail.ru'
					value={email}
					onChange={handleEmailChange}
					disabled={status?.type === 'info'}
				/>
			</Form.Item>

			<Form.Item style={{ marginBottom: 12 }}>
				<Button
					type='primary'
					htmlType='submit'
					block
					size='large'
					disabled={!isValidEmail(email)}
					loading={status?.type === 'info'}
				>
					{tempSkipEmail ? 'Войти' : t('auth.getCodeEmail')}
				</Button>
			</Form.Item>
			<p style={{ marginBottom: 8, marginTop: 0 }}>
				{t('auth.privacyAgreement')}
			</p>
			{status?.type && (
				<Alert message={status.text} type={status.type} banner />
			)}
		</Form>
	);
};

const EmailCodeForm = ({ handleCode, handleClearCode, email, status, setStatus, resend }) => {
	const { t } = useTranslation();

	function fmtMSS(s) {
		return (s - (s %= 60)) / 60 + (9 < s ? ':' : ':0') + s;
	}

	const AgainCode = () => {
		const [timer, setTimer] = useState(45);
		const handleResend = async () => {
			setTimer(45);
			await resend();
		};
		useEffect(() => {
			const interval = setInterval(() => {
				if (timer > 0) setTimer(timer - 1);
			}, 1000);
			return () => clearInterval(interval);
		}, [timer]);

		return (
			<p>
				{timer === 0 ? (
					<Button type='primary' size='large' onClick={handleResend}>
						{t('auth.resendCode')}
					</Button>
				) : (
					<a href='#' onClick={e => e.preventDefault()}>
						{t('auth.resendCodeTimer', { time: fmtMSS(timer) })}
					</a>
				)}
			</p>
		);
	};

	return (
		<Form name='signin-email-code' layout='vertical'>
			<Typography.Title
				level={5}
				className='text-gray'
				style={{ marginTop: 0, marginBottom: 12 }}
			>
				{t('auth.codeSentToEmail')}
				<br />
				{email}
			</Typography.Title>
			<Form.Item
				name='code'
				label={t('auth.codeFromEmail')}
				rules={[{ required: true, message: t('auth.enterCode') }]}
				style={{ marginBottom: 16 }}
			>
				<OTPInput
					onChange={handleCode}
					numInputs={4}
					inputType='number'
					shouldAutoFocus
					renderSeparator={<span className='ml-10 mr-10'>-</span>}
					renderInput={props => (
						<Input
							{...props}
							className='rn-otp-input'
							disabled={status && status.type === 'info'}
						/>
					)}
				/>
			</Form.Item>
			<AgainCode />
			<p className='text-gray' style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
				{t('auth.checkSpamHint')}
			</p>
			{status?.type && (
				<Alert message={status.text} type={status.type} banner />
			)}
		</Form>
	);
};

export default SignIn;
